import { z } from 'zod';
import { AdminId } from './admin.js';
export const AuditAction = z.enum([
    'admin.login',
    'admin.logout',
    'admin.session_expired',
    'admin.created',
    'admin.role_changed',
    'admin.disabled',
    'admin.password_changed',
    'channel_account.created',
    'channel_account.updated',
    'channel_account.deleted',
    'channel_account.oauth_reconnected',
    'topic.created',
    'topic.updated',
    'topic.deleted',
    'topic.restored',
    'pipeline.created',
    'pipeline.updated',
    'pipeline.deleted',
    'pipeline.run_requested',
    'conversation.status_changed',
    // Operator sent an outbound e-mail to the customer (POST /:id/reply) — the
    // most direct customer-facing action there is, and until now the only one the
    // log was blind to. The diff carries the conversation, the persisted message
    // id and the template (when one was used) and NEVER the body or the customer's
    // address: the log answers «who sent what to whom», not «what was written».
    'conversation.replied',
    'conversation.merged',
    // Слияние ВСЕХ живых диалогов контакта в один (`POST /:id/merge-all`).
    // Отдельное действие, а не N строк `conversation.merged`: оператор принял
    // ОДНО решение над набором, и лог обязан отвечать «кто схлопнул контакт», а
    // не «кто двенадцать раз слил по одному». В diff — `sourceIds` целиком плюс
    // то, что необратимо: сколько сообщений переехало и какие отложенные письма
    // и прогоны пайплайнов были отменены (дубликаты гасятся молча, и без этих
    // списков «письмо не ушло» никак не связать со слиянием).
    'conversation.merged_many',
    'blacklist.added',
    'blacklist.removed',
    'subscription.cancelled',
    'telegram.rerender_all',
    'telegram_destination.updated',
    'telegram_destination.deleted',
    'scheduled_email.created',
    'scheduled_email.cancelled',
    'llm_settings.updated',
    'conversation.reclassify_requested',
    // Ручное переопределение вердикта модели о том, в каком качестве пишет
    // отправитель (supervisor+). В diff — только переход {from, to}: лог отвечает
    // на вопрос «кто и что переключил», а не «что было в письме».
    'conversation.sender_capacity_overridden',
    // Manual operator bind of a billing candidate to the conversation's contact.
    // The diff carries `witUserId` + the conversation and NEVER the candidate's
    // e-mail or card digits — the audit log answers «who bound whom», not «what
    // was on the screen».
    'identity.attached',
    'reply_template.created',
    'reply_template.updated',
    'reply_template.deleted',
    'email_layout.updated',
    // Раздел «Чарджбеки». Привязка клиента к кейсу — тот же класс действия, что
    // `identity.attached`: она определяет, чьё биллинговое состояние оператор
    // увидит и по чьему кейсу примет решение. В diff — только `witUserId` и
    // кейс, никогда email кандидата и цифры карты: лог отвечает «кто кого
    // привязал», а не «что было на экране».
    'risk_case.identity_attached',
    'risk_case.identity_detached',
    'risk_case.rematch_requested',
    // Смена нашего статуса разбора кейса. В diff — переход {from, to}, как у
    // `conversation.sender_capacity_overridden`: лог отвечает «кто и на что
    // переключил», и без `from` запись «поставил resolved» не отличает разбор
    // от отмены чужого вывода.
    'risk_case.status_changed',
    // Ручной запрос (пере)оценки реакции по кейсу (task 12,
    // `POST /:id/react`). Само событие ничего не отправляет наружу — оно лишь
    // просит воркер пересчитать реакцию; необратимые шаги (возврат, отмена
    // подписки, ответ провайдеру), которые из этого запроса могут
    // последовать, логируются реактором отдельно.
    'risk_case.reaction_requested',
    // Отправка reason-кода в источник. НЕОБРАТИМА — провайдер учитывает только
    // первый апдейт, поэтому в логе обязаны быть и успех, и неудача.
    'risk_alert.action_sent',
    // Правила реакций (`risk_reaction_rule`) — task 12. Условия и шаги правила
    // ходят по кейсам БЕЗ оператора, поэтому каждое изменение (включая
    // перестановку порядка — первое совпавшее включённое правило выигрывает)
    // обязано быть в логе так же, как правки аккаунтов источников ниже.
    'risk_reaction_rule.created',
    'risk_reaction_rule.updated',
    'risk_reaction_rule.deleted',
    'risk_reaction_rule.reordered',
    // Тумблеры автоматики реакций (`risk.reactions_enabled` и соседи,
    // `PUT /api/chargebacks/automation`). ОДНО действие на все четыре флага —
    // тот же довод, что у `system_settings.updated` ниже: в diff и так лежит,
    // какие флаги изменились и в какую сторону. Логируется отдельно от правил
    // потому, что это другое решение: правило отвечает «что делать с кейсом»,
    // а тумблер — «делаем ли мы это вообще» (включение «Боевых реакций» — это
    // момент, с которого автоматика начинает возвращать деньги и отвечать
    // провайдеру, и в логе он обязан быть отличим от правки правила).
    'risk_automation_settings.updated',
    // Аккаунты источников риска. В diff — только ИМЕНА изменённых полей и факт
    // ротации ключа (`apiKeyRotated: true`), никогда сами значения: лог отвечает
    // «кто и что переключил», а хранить секрет во второй таблице — значит завести
    // второе место, где его надо защищать.
    'risk_account.created',
    'risk_account.updated',
    'risk_account.deleted',
    // Порог крупного плательщика («кита»). Число одно, но глобальное: правка
    // сразу меняет метку в инбоксе, метрики дашборда и ветки авто-пайплайнов —
    // и снаружи это выглядит не как изменение, а как «сегодня всё иначе».
    // Поэтому в diff, кроме {from, to}, лежит `wasDefault`: «6 → 8» из значения
    // по умолчанию и «6 → 8» из явно выставленного порога — разные истории.
    'billing_settings.updated',
    // Общесистемные флаги `/api/system-settings` (склейка алертов платёжного
    // риска, напоминание в Telegram). Одно действие на весь роут, а не по
    // действию на флаг: в diff и так лежит, какие поля изменились и в какую
    // сторону, а разбор лога по имени действия только раздробил бы историю.
    //
    // Тиры записи у флагов РАЗНЫЕ (склейка — владелец, уведомления —
    // супервайзер+), и `actorRole` в строке лога отвечает на «кто именно» без
    // отдельного имени действия.
    'system_settings.updated',
    // Справочник тегов на диалогах. Три действия, а не одно: создание и
    // переименование задевают ОДИН тег, а удаление снимает его со ВСЕХ диалогов
    // сразу (в diff — `removedFromConversations`, то есть цена действия), и в
    // логе это обязано читаться как разные события, а не как «правка тега».
    'tag.created',
    'tag.renamed',
    'tag.deleted',
    // Простановка/снятие тегов на КОНКРЕТНОМ диалоге. В diff — только ИМЕНА
    // добавленных и снятых тегов (`{added, removed}`), не итоговый набор:
    // вопрос лога — «что оператор изменил», а текущий набор и так лежит в
    // `conversation_tag`.
    'conversation.tags_changed',
    // Внутренние (операторские) комментарии к диалогу. Текст в diff НЕ пишется:
    // тот же довод, что у `conversation.replied` выше — лог отвечает «кто и где
    // оставил запись», а сама запись лежит в `conversation_comment`. Удаление
    // логируется отдельно потому, что это единственный путь, на котором текст
    // исчезает, и по `resourceType: 'conversation_comment'` его видно в истории
    // ресурса, которого больше нет.
    'conversation.comment_added',
    'conversation.comment_deleted',
    // Правка текста. В diff — ТОЛЬКО длина тела до/после
    // (`{ bodyLength: { from, to } }`), самого текста нет по тому же доводу.
    'conversation.comment_edited',
    // Раздел «Контент» (контент-движок `wit_video_combine`). Каждая правка
    // меняет БУДУЩИЕ публикации всех аккаунтов, а данные живут в движке, не у
    // нас: этот лог — единственное место, где видно, кто из админки что
    // поменял. Сбой движка пишется в `diff.outcome`, а не теряется: «нажал, но
    // не применилось» тоже ответ на вопрос «почему клип снова первым».
    //
    // Переопределения клипа: создание и снятие — разные события (снятие
    // возвращает клип в выбор, и в логе это обязано читаться отдельно).
    'content_override.created',
    'content_override.deleted',
    // Подпись/хэштеги публикации. В diff — {from, to} текста: подпись — не
    // данные клиента, а публичный текст поста, и без «было» правку не откатить.
    'content_caption.updated',
    // Смена правила выбора по сети (в т.ч. `auto` — снятие ручного выбора) и
    // правка трёх параметров — разные действия: правило переключает модель,
    // параметры двигают пороги внутри неё.
    'content_rule.updated',
    'content_rule.params_updated',
    // «Решено» по тревоге движка. Тревога не исчезает — движок ставит
    // `resolvedAt`, и при повторении проблемы откроет новую; в логе важно, кто
    // счёл проблему закрытой, если она вернулась.
    'content_alert.resolved',
    // Ручной запуск сбора статистики: ничего не меняет в настройках, но нагружает
    // внешние API соцсетей — в логе должно быть видно, кто и когда его дёргал.
    'content_collector.run_requested',
    // Эксперименты движка (плечи параметров по аккаунтам-неделям): создание
    // запускает рандомизацию будущих публикаций, правка — остановку или новый
    // конец. Разные события: «кто запустил» и «кто остановил» — разные вопросы.
    'content_experiment.created',
    'content_experiment.updated',
    // Настройки аккаунта «Длинные ролики и обложки» (включение длинного ролика,
    // длительности, число клипов, обложки). Меняют, ЧТО и КАК движок будет
    // публиковать раз в неделю без участия человека. В diff — только изменённые
    // поля {from, to}; «было» читается до записи, иначе правку не откатить.
    'content_account_settings.updated',
    // Раздел «Соцсети» (модуль комментариев движка). Данные — в движке, этот
    // лог — единственное место, где видно, кто из админки что сделал.
    //
    // Подключение токена соцсети к аккаунту: вставка/замена, проверка,
    // отключение — три разных события. Замена действующего токена необратима
    // для чужой сессии, отключение останавливает опрос и действия аккаунта.
    // В diff — `{account, network, authType, tokenRotated: true,
    // externalUsername}` и НИКОГДА сам токен: тот же довод, что у
    // `risk_account.updated` — хранить секрет во второй таблице значит завести
    // второе место, где его надо защищать.
    'social_connection.updated',
    // Проверка токена ходит в сеть и может перевести подключение в `invalid`;
    // в diff — переход статуса {from, to}.
    'social_connection.checked',
    'social_connection.deleted',
    // «Подключить тем же токеном, что у Instagram» (FB-SPEC §0.4): движок сам
    // копирует зашифрованный токен подключения другой сети того же аккаунта и
    // проверяет его. Только владелец. В diff — `{account, network, authType,
    // fromNetwork, externalUsername, externalName, hasPageToken}` и НИКОГДА
    // токен (он через admin-api вообще не проходит).
    'social_connection.reused',
    // «Возобновить» аккаунт после повторной блокировки сети (368): движок
    // снимает `blockedAt` и паузу, действия аккаунта снова могут уходить в сеть.
    // Только владелец, после подтверждения «ограничение снято в приложении
    // Instagram». В diff — `{account, network, blockedAt, stoppedAt,
    // pausedUntil}` (читается до записи) и `cleared`/`remaining` — какие паузы
    // движок снял и какие остались (коды причин из ответа движка).
    'social_connection.resumed',
    // «Остановить аккаунт» (RULINGS-SAFETY-2): действия одного аккаунта × сети
    // не уходят в сеть, пока владелец не нажмёт «Возобновить». Выключающее
    // действие — супервайзер и владелец (`social.manage`). В diff —
    // `{account, network, stopped: {from, to}}` («было» — `stoppedAt` до стопа).
    'social_connection.stopped',
    // Настройки реагирования (режим выкл/вручную/авто, алгоритм,
    // автоудаление, лимиты). Перевод в `auto` значит «действия уходят в сеть
    // без человека», поэтому в diff — изменённые поля {from, to}.
    'social_settings.updated',
    // Действия над комментариями: запрос (ручное действие или «Повторить»),
    // подтверждение и отклонение предложенного алгоритмом — разные события:
    // «кто нажал удалить» и «кто одобрил удаление, которое предложил алгоритм» —
    // разные вопросы. Текст ответа в diff ПИШЕТСЯ: это публичный текст под
    // нашим постом, как подпись, а не данные клиента.
    'social_action.requested',
    'social_action.approved',
    'social_action.rejected',
    // «Отметить просмотренным»: человек решил, что на комментарий реагировать
    // не нужно, и он уходит из «Ждут решения». В сеть ничего не уходит, но
    // комментарий больше не попадётся на глаза — в логе должно быть видно, кто
    // так решил. В diff — `{commentId, account, network, state}`, без текста
    // комментария (это текст клиента, а не наш).
    'social_comment.dismissed',
    // Ручной опрос комментариев: ничего не меняет в настройках, но расходует
    // лимиты API соцсети — в логе должно быть видно, кто и когда его дёргал
    // (тот же довод, что у `content_collector.run_requested`).
    'social_poll.run_requested',
    // Общий «Стоп всех действий» / «Возобновить все действия»: стоп-кран на ВСЕ
    // действия всех аккаунтов во всех сетях, без перезапуска движка. Остановить
    // — супервайзер и владелец (`social.manage`), возобновить — только владелец.
    // Два разных события: «кто остановил» и «кто запустил снова» — разные
    // вопросы при разборе блокировки. В diff — `{stopped: {from, to}}`.
    'social_global.stopped',
    'social_global.resumed',
    // Раздел «Бухгалтерия» (квартальный НДС, расчёт в wit-payment-service).
    // Пересчёт переписывает суммы черновика квартала, которые потом вводятся в
    // налоговый портал: если цифры «поплыли» между двумя просмотрами, лог
    // отвечает, кто запускал пересчёт (плановщик пишется в самой платёжке как
    // `computedBy: scheduler`). Отметка «подан» НЕОБРАТИМА — суммы региона
    // фиксируются навсегда, снять её через API нельзя; это действие с
    // юридическим весом, и в логе обязано читаться отдельно от пересчёта: кто,
    // когда и с каким примечанием объявил квартал поданным. Сбой платёжки
    // пишется в `diff.outcome`, а не теряется.
    'accounting_quarter.recalculated',
    'accounting_quarter.filed',
    // Раздел «Запуски FB» (конструктор запусков auto-ads, `/api/ads/*`).
    // Данные живут в auto-ads, этот лог — единственное место, где видно, кто
    // из админки что сделал. Каждая запись — и на успехе, и на сбое (сбой —
    // в `diff.outcome`). В diff — ТОЛЬКО машинные поля: id, версии, счётчики,
    // коды сбоя; тексты креативов не пишутся никогда.
    //
    // Шаблоны: создание (в т.ч. дубликат — `duplicateOf`), правка
    // (`version`), архив и возврат из архива — разные события.
    'ads_template.created',
    'ads_template.updated',
    'ads_template.archived',
    'ads_template.unarchived',
    // Профиль кабинета (страница, Instagram, пиксель, слоты аудиторий) —
    // меняет, куда пойдут ВСЕ будущие запуски кабинета.
    'ads_account_profile.updated',
    // Медиатека: начало загрузки, её завершение и отмена — разные события
    // («начал и бросил» видно в логе), архив файла, повтор переноса в
    // кабинет. Переноса между кабинетами вручную и импорта нет (перенос
    // автоматический — спека v3 §2), поэтому и событий для них нет.
    'ads_media.upload_started',
    'ads_media.uploaded',
    'ads_media.upload_aborted',
    'ads_media.archived',
    'ads_media.retried',
    // Пачки: создание тратит деньги (при `activation: 'auto'` — без
    // дальнейшего участия человека), поэтому в diff — `batchId`, число
    // запусков, режим включения и `templateId@version`.
    'ads_batch.created',
    'ads_batch.executed',
    'ads_batch.cancelled',
    // Запуски. `force_activated` отделено от `activated`: включение ВОПРЕКИ
    // расхождениям сверки — решение с другим весом, в diff — число ошибок.
    'ads_launch.activated',
    'ads_launch.force_activated',
    'ads_launch.paused',
    'ads_launch.rolled_back',
    'ads_launch.retried',
    'ads_launch.reverified',
    // Кампании, наборы и объявления вне запусков конструктора (MCP `launch_campaign_*`):
    // пауза и включение одного объекта, дубль кампании в одноразовый шаблон.
    'ads_campaign.paused',
    'ads_campaign.resumed',
    'ads_campaign.duplicated',
    // Списки стран — общие с правилами SCALE: правка меняет гео будущих
    // клонов SCALE, а не только запусков конструктора.
    'ads_country_list.created',
    'ads_country_list.updated',
    'ads_country_list.deleted',
]);
export const AuditEntry = z.object({
    id: z.guid(),
    actorId: AdminId.nullable(),
    actorRole: z.string().nullable(),
    action: AuditAction,
    resourceType: z.string().nullable(),
    resourceId: z.string().nullable(),
    diff: z.record(z.string(), z.unknown()).nullable(),
    ip: z.string().nullable(),
    requestId: z.string().nullable(),
    renderedTimezone: z.string().nullable(),
    tenantId: z.string().nullable(),
    createdAt: z.string().datetime(),
});
//# sourceMappingURL=audit.js.map